import type {
  AnnouncementKind,
  IAnnouncement,
  IAnnouncementBoardItemLink,
  IAnnouncementFeed,
  IAnnouncementInternal,
  INotifyRequestersResult,
} from '@repo/types'

import { createApiCall, statusOf } from './index'

export type {
  AnnouncementKind,
  IAnnouncement,
  IAnnouncementBoardItemLink,
  IAnnouncementFeed,
  IAnnouncementInternal,
  INotifyRequestersResult,
}

/**
 * "What's new" API client (VRG-221).
 *
 * Mirrors `supportApi` deliberately, including the error class and the `ok()` guard —
 * see below for why that guard is not optional.
 */
export class AnnouncementsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'AnnouncementsApiError'
  }
}


/**
 * True when the announcement is not there FOR THIS READER — deleted, unpublished, or
 * gated away from them. The API deliberately returns the same 404 for all three, so
 * this predicate cannot and must not try to tell them apart.
 *
 * 400 counts too. A `/whats-new/<garbage>` URL fails `ParseUUIDPipe` at the edge and
 * comes back 400, but to the person who followed a truncated link that is the same
 * situation as a stale id — a link that does not resolve. Rendering "couldn't load,
 * try again" for it would invite them to retry a URL that can never work.
 */
export const isMissing = (error: unknown): boolean => {
  const status = statusOf(error)
  return status === 404 || status === 400
}

/**
 * Unwraps the response body.
 *
 * The status guard is now belt-and-braces: `createApiCall` rejects on every >= 400 (VRG-386),
 * so a non-2xx no longer reaches this function at all. It is kept because it costs nothing and
 * still catches a malformed 2xx.
 *
 * What the guard must NOT be relied on for is constructing the error the predicates above read
 * — it no longer runs on the failure path, which is exactly why `isForbidden`/`isMissing` were
 * rebased onto the status. The stake is unchanged from when this was written: the internal
 * routes 403 for everyone until the permission is granted, so a mis-shaped error here is the
 * DEFAULT state of this surface, not an edge case, and it renders as "nothing here yet" —
 * telling a staffer there are no announcements when there are several they simply cannot see.
 */
const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new AnnouncementsApiError(response?.data?.message || fallback, response?.status ?? 0)
  }
  return response.data
}

export interface AnnouncementWritePayload {
  title?: string
  body?: string
  kind?: AnnouncementKind
  ctaLabel?: string | null
  ctaRoute?: string | null
  tourKey?: string | null
  /**
   * LEGACY (VRG-332). The editor only ever sends `null` here — to CLEAR a gate written
   * before the role gate existed. It never sets a new one.
   */
  requiredPermission?: string | null
  /** Role names. `null` or `[]` clears the gate; omit to leave it untouched. */
  requiredRoleNames?: string[] | null
  boardItemIds?: string[] | null
}

export const announcementsApi = {
  // ── Company-facing ────────────────────────────────────────────────────────

  /**
   * The viewer's feed: every published announcement they are permitted to see and
   * that postdates their signup, plus a single `unseenLatest` for the modal.
   *
   * One call serves BOTH surfaces (the modal and /whats-new) because they render the
   * same card from the same data — a second "just the modal one" endpoint would be a
   * second place for the four rules to be applied slightly differently.
   */
  async feed(): Promise<IAnnouncementFeed> {
    const response = await createApiCall({ method: 'GET', url: '/announcements' })
    return ok(response, "Couldn't load What's new")
  },

  /**
   * ONE announcement, by id — behind the shareable `/whats-new/:id` page.
   *
   * NOT served from the feed's cache, which is the cheaper-looking option and is wrong:
   * `feed()` is filtered to announcements published AFTER the caller's signup, so an
   * older announcement is absent from it entirely. Looking the id up there would 404
   * precisely the case a shareable link exists for — sending an older announcement to a
   * newer colleague. The endpoint applies the audience gate without the signup cutoff.
   *
   * A gated or non-existent announcement both come back 404 (see the API), so the page
   * cannot tell them apart and neither can a reader probing ids.
   */
  async get(id: string): Promise<IAnnouncement> {
    const response = await createApiCall({ method: 'GET', url: `/announcements/${id}` })
    return ok(response, "Couldn't load that announcement")
  },

  /** Dismiss one announcement for the current user, permanently. Idempotent. */
  async markSeen(id: string): Promise<{ seen: true }> {
    const response = await createApiCall({ method: 'POST', url: `/announcements/${id}/seen` })
    return ok(response, "Couldn't mark that as seen")
  },

  // ── Internal (staff) ──────────────────────────────────────────────────────

  async internalList(): Promise<IAnnouncementInternal[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/announcements' })
    return ok(response, 'Failed to load announcements')
  },

  /**
   * ONE announcement, drafts included — the read behind `/internal/announcements/:id/edit`.
   *
   * Not served from `internalList()`'s cache, for the same reason the company-facing
   * `get()` above is not served from `feed()`: a staffer can arrive at the edit URL
   * directly (a pasted link, a reload, a browser back into the form) with no list ever
   * having been fetched in this session. Reading the id out of a cache that may be empty
   * would render an empty form over a real record, and the first Save would look like an
   * edit while actually clearing every field the form had defaulted to blank.
   */
  async internalGet(id: string): Promise<IAnnouncementInternal> {
    const response = await createApiCall({
      method: 'GET',
      url: `/internal/announcements/${id}`,
    })
    return ok(response, "Couldn't load that announcement")
  },

  async internalBoardItems(): Promise<IAnnouncementBoardItemLink[]> {
    const response = await createApiCall({
      method: 'GET',
      url: '/internal/announcements/board-items',
    })
    return ok(response, 'Failed to load feature requests')
  },

  async internalCreate(payload: AnnouncementWritePayload): Promise<IAnnouncementInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/announcements',
      data: payload,
    })
    return ok(response, "Couldn't create that announcement")
  },

  async internalUpdate(
    id: string,
    payload: AnnouncementWritePayload,
  ): Promise<IAnnouncementInternal> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/internal/announcements/${id}`,
      data: payload,
    })
    return ok(response, "Couldn't save that announcement")
  },

  async internalSetPublished(id: string, published: boolean): Promise<IAnnouncementInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/announcements/${id}/publish`,
      // Always explicit. The API refuses a missing/non-boolean value, because
      // defaulting a publish to `true` means a malformed request broadcasts to every
      // brand — the one outcome here that cannot be taken back.
      data: { published },
    })
    return ok(response, published ? "Couldn't publish that" : "Couldn't unpublish that")
  },

  async internalNotifyRequesters(id: string): Promise<INotifyRequestersResult> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/announcements/${id}/notify-requesters`,
    })
    return ok(response, "Couldn't notify the requesters")
  },

  async internalRemove(id: string): Promise<{ deleted: true }> {
    const response = await createApiCall({
      method: 'DELETE',
      url: `/internal/announcements/${id}`,
    })
    return ok(response, "Couldn't delete that announcement")
  },
}
